import React from "react";
import SubTitle from "../../../components/SubTitle";
import {useGetQuery, useRemoveMutation} from "../../../hooks/useQueryHooks";
import {ResponseList, ResponseRemove} from "../../../models/clientModels/interfaces/Response";
import {
  EDCategory,
  initialEDCategoryListParams,
} from "../../../models/serverModels/education/EDCategory";

import {
  Breadcrumbs,
  Button,
  Link as Linked,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {useNavigate, useParams} from "react-router-dom";
import * as categoryListApi from "../../../api/educationApi/educationCategoryApi";
import {SubMenuGroup} from "../../../styledComponents/StyledListContainer";

import HomeIcon from "@mui/icons-material/Home";
import WhatshotIcon from "@mui/icons-material/Whatshot";

import {useQueryClient} from "react-query";
import {useRecoilState, useRecoilValue, useSetRecoilState} from "recoil";
import {alertStateAtom} from "../../../Recoil/Atom/AlertAtom";
import {middleCategoryIdState} from "../../../Recoil/Atom/CategoryListAtom";
import {tokenSelector} from "../../../Recoil/Atom/authState";
import * as categoryApi from "../../../api/educationApi/educationCategoryApi";
import MWSkeleton from "../../../components/MWSkeleton";
import * as Params from "../../../models/clientModels/interfaces/apiParams";
import DataSaverOnIcon from "@mui/icons-material/DataSaverOn";
import {StyledNoneScrollTable, StyledNoneStickyTable} from "../../../styledComponents/StyledTable";

const MiddleCategoryList = () => {
  const queryClient = useQueryClient();

  const {param} = useParams();
  const navigate = useNavigate();
  const depth = 1;

  const tokenState = useRecoilValue(tokenSelector);

  const setAlertState = useSetRecoilState(alertStateAtom);

  const [middleCategoryId, setMiddleCategoryId] = useRecoilState(middleCategoryIdState);

  // region: Query (교육과정 카테고리 리스트)

  const {
    data: middleCategoryList,
    isLoading,
    isFetching,
  } = useGetQuery<ResponseList<EDCategory>>({
    key: ["EducationCategoryList", param],
    api: () =>
      categoryListApi.getList({
        ...initialEDCategoryListParams,
        token: tokenState,
        depth: 1,
        parentUID: parseInt(param!),
      }),
  });

  const {mutate: removeMutate} = useRemoveMutation<ResponseRemove, Params.Remove>({
    api: categoryApi.remove,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries("EducationCategoryList");
      },
    },
  });

  return (
    <React.Fragment>
      <SubTitle subTitle="중 분류목록" />
      <SubMenuGroup>
        <div role="presentation">
          <Breadcrumbs aria-label="breadcrumb" sx={{paddingLeft: "8px"}}>
            <Linked sx={{display: "flex", alignItems: "center"}} href="/education/categoryList">
              <HomeIcon sx={{mr: 0.5}} fontSize="inherit" />
              대분류
            </Linked>
            <Linked underline="none" sx={{display: "flex", alignItems: "center"}}>
              <WhatshotIcon sx={{mr: 0.5}} fontSize="inherit" />
              중분류
            </Linked>
          </Breadcrumbs>
        </div>
        <Button
          color="primary"
          variant="outlined"
          size="small"
          startIcon={<DataSaverOnIcon />}
          onClick={() => {
            navigate(`/Education/Create/${depth}/${param}`);
          }}
        >
          추가
        </Button>
      </SubMenuGroup>
      <StyledNoneScrollTable>
        <Table>
          <StyledNoneStickyTable>
            <TableRow>
              <TableCell align="center">#</TableCell>
              <TableCell align="center">이름</TableCell>
              <TableCell align="center">분류</TableCell>
              <TableCell align="center">편집</TableCell>
            </TableRow>
          </StyledNoneStickyTable>
          <TableBody>
            {isLoading && isFetching ? (
              <MWSkeleton row={12} column={4} />
            ) : (
              middleCategoryList?.items.map((item, index) => (
                <TableRow key={item.uid}>
                  <TableCell align="center">{index + 1}</TableCell>
                  <TableCell align="center">{item.name}</TableCell>
                  <TableCell align="center">
                    <Button
                      onClick={() => {
                        navigate(`/Education/subCategoryList/${item.uid}`);
                        setMiddleCategoryId(item.parentUID);
                      }}
                    >
                      하위분류
                    </Button>
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      sx={{marginRight: "16px"}}
                      onClick={() => {
                        navigate(`/Education/Update/${depth}/${param}`, {state: item.uid});
                      }}
                    >
                      편집
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => {
                        setAlertState({
                          toggle: true,
                          message: "중분류를 삭제하시겠습니까?",
                          execution: () => removeMutate({token: tokenState, id: item.uid}),
                        });
                      }}
                    >
                      삭제
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </StyledNoneScrollTable>
    </React.Fragment>
  );
};

export default MiddleCategoryList;
